import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import styles from './FirstPlanShell.AccountMenu.module.css';

/**
 * Part 7 (onboarding_2_0_v1), B465: Sign out leaves the top row and sits behind
 * the account button. One tap opens a one-item menu, one tap signs out.
 *
 * The reveal (Step7Welcome), the first look (FirstPlanShell) and the wizard's own
 * restyled shell (`OnboardingShellV2`, B490: it carried a second copy of this menu until
 * then) all draw this one. Behind the flag only: nothing renders this with the flag off.
 *
 * The button is 44 by 44 and wears the athlete's initials (the ratified Part 1
 * avatar), or a person glyph when there are none. The accessible name is the
 * new string "Account" (page strings list, shell).
 */

/** Up to two letters: the first word's and the last word's first letters. */
export function accountInitials(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = Array.from(words[0]!)[0] ?? '';
  const last = words.length > 1 ? (Array.from(words[words.length - 1]!)[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

type AccountMenuProps = {
  /** The name the initials come from; absent or blank draws the person glyph. */
  name?: string | null;
  onSignOut: () => void | Promise<void>;
  /**
   * Fix round 1 (security NOTE C): the reveal passes true while its completion is being
   * saved. The item stays in the menu, visibly unavailable, with the reason under it, and
   * returns the moment the save settles or fails. Absent means the item is always live
   * (the first look).
   */
  signOutDisabled?: boolean;
  /**
   * The reason printed under the unavailable item. Absent means the reveal's sentence below;
   * the wizard's shell passes its own (the under-13 removal is what holds Sign out there).
   */
  waitSentence?: string;
};

/** The reason Sign out is unavailable while the reveal saves. The reveal's own button
 *  reads "Setting up…" for the same stretch. */
export const SIGN_OUT_SAVING_SENTENCE = 'You can sign out once setup finishes.';

export function AccountMenu({ name, onSignOut, signOutDisabled, waitSentence = SIGN_OUT_SAVING_SENTENCE }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const itemRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();
  const noteId = useId();
  const initials = accountInitials(name);

  // Focus lands on the one item when the menu opens, as a menu button should. A disabled
  // item cannot hold focus, so the button keeps it and the sentence is read with the item.
  useEffect(() => {
    if (open && !signOutDisabled) itemRef.current?.focus();
  }, [open, signOutDisabled]);

  // A press anywhere outside closes it, and so does Escape wherever focus is (the root's own key
  // handler below covers focus inside the menu; this covers focus that has gone elsewhere).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: Event) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const closeToButton = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (!open) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeToButton();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      itemRef.current?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <span className={styles.root} ref={rootRef} onKeyDown={onKeyDown}>
      <button
        type="button"
        ref={buttonRef}
        className={styles.button}
        aria-label="Account"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {initials !== '' ? (
          <span aria-hidden="true">{initials}</span>
        ) : (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="10" cy="7" r="3.5" fill="currentColor" />
            <path d="M3.5 17c.6-3.2 3.2-5 6.5-5s5.9 1.8 6.5 5" fill="currentColor" />
          </svg>
        )}
      </button>
      {open ? (
        <div className={styles.menu} role="menu" id={menuId}>
          <button
            type="button"
            ref={itemRef}
            role="menuitem"
            className={styles.item}
            disabled={signOutDisabled}
            aria-disabled={signOutDisabled || undefined}
            aria-describedby={signOutDisabled ? noteId : undefined}
            onClick={() => {
              if (signOutDisabled) return;
              setOpen(false);
              void onSignOut();
            }}
          >
            Sign out
          </button>
          {signOutDisabled ? (
            <p id={noteId} className={styles.note}>
              {waitSentence}
            </p>
          ) : null}
        </div>
      ) : null}
    </span>
  );
}
